import { useCallback, useMemo, useState } from 'react'
import { useFormPacketsEnabled } from '@user/store/tenantFeatures/hooks/useFormPacketsEnabled'
import { DashboardAgreement } from '@user/store/dashboard/widgets/manageAgreements/typings'
import { setActiveRequirementActionCreator } from '@user/components/RequirementsToClose/state'
import { useAppDispatch } from '@user/store/hooks'
import { FM_FORM_STATUSES } from '@user/store/agreements'
import { getAgreements } from '@user/store/dashboard/widgets/manageAgreements/api'
import { useSelector } from 'react-redux'
import { selectManageAgreements } from '@user/store/dashboard/widgets/manageAgreements/manageAgreements'
import {
  IUserPreferenceGeneralListItemType,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'

export function useData() {
  const [createDrawerOpen, setCreateDrawerOpen] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const [agreement, setAgreements] = useState<DashboardAgreement | undefined>(
    undefined,
  )
  const [cancelPopupOpen, setCancelPopupOpen] = useState<boolean>(false)
  const formPacketEnabled = useFormPacketsEnabled()
  const { page } = useSelector(selectManageAgreements)

  const userGeneralPreferences = useSelector(userGeneralPreferencesList)

  const filterValue = useMemo(
    () =>
      userGeneralPreferences?.find(
        (row) =>
          row?.type ==
          IUserPreferenceGeneralListItemType.ManageAgreementWidgetFilter,
      )?.value ?? FM_FORM_STATUSES.All,
    [userGeneralPreferences],
  )

  const [svcState, setSvcState] = useState<{
    svcOpen: boolean
    isAddingService: boolean
  }>(() => ({
    svcOpen: false,
    isAddingService: false,
  }))

  const onOpenSvc = useCallback(() => {
    setSvcState({
      svcOpen: true,
      isAddingService: false,
    })
  }, [])

  const onCloseSvc = useCallback(() => {
    dispatch(setActiveRequirementActionCreator(undefined))

    setSvcState({
      svcOpen: false,
      isAddingService: false,
    })
  }, [dispatch])

  const openAgreementCreateDrawer = useCallback(
    (agreement: DashboardAgreement) => {
      setCreateDrawerOpen(true)
      setAgreements(agreement)
      dispatch(setActiveRequirementActionCreator('agreement'))
    },
    [dispatch],
  )

  const closeCreateDrawer = useCallback(() => {
    setCancelPopupOpen(true)
  }, [])

  const refreshAgreementList = useCallback(
    (status?: FM_FORM_STATUSES) => {
      const effectiveStatus = status ?? filterValue

      const query =
        effectiveStatus === FM_FORM_STATUSES.All ? '' : effectiveStatus

      dispatch(
        getAgreements({
          page,
          query,
        }),
      )
    },
    [dispatch, filterValue, page],
  )

  const onClose = useCallback(() => {
    setCreateDrawerOpen(false)
    setCancelPopupOpen(false)
    refreshAgreementList()
  }, [refreshAgreementList])

  const hideCancelDialog = useCallback(() => {
    setCancelPopupOpen(false)
  }, [])

  return {
    createDrawerOpen,
    setCreateDrawerOpen,
    formPacketEnabled,
    openAgreementCreateDrawer,
    agreement,
    closeCreateDrawer,
    cancelPopupOpen,
    onClose,
    onCloseSvc,
    onOpenSvc,
    hideCancelDialog,
    refreshAgreementList,
    statusValue: filterValue as FM_FORM_STATUSES,
    ...svcState,
  }
}
